import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import has from 'lodash/has';

import { Cell, Grid } from 'formlabs-web-components/Grid';
import { Picture } from 'formlabs-web-components/Media';

import Price from '../Price';
import ProductOptionControl, {
  ProductOptionListControl
} from '../ProductOptionControl';
import useProductDetails from './useProductDetails';

import '../ProductDetailPlugin.module.css';

const ProductOptionCard = ({
  recommended,
  combine,
  title,
  subtitle,
  required,
  type,
  handleOptionChange,
  option,
  images,
  step,
  currentStep,
  amount,
  forPrinter,
  bundle,
  optionData
}) => {
  const [t] = useTranslation();

  // optionData.fetchDetails is opting in to fetch details
  const { data: linkedProductsWithDetails } = useProductDetails(
    optionData,
    option['product_links'] || []
  );

  const { storeId } = useSelector(state => ({
    storeId: state.store.id
  }));
  // Strike through prices in UK must be prefixed with "RRP"
  const isUKStore = storeId === 'gb_en';

  const defaultvalue =
    bundle.get(option.option_id) && bundle.get(option.option_id).size > 0;
  const [selectedItems, setSelectedItems] = useState(
    defaultvalue ? bundle.get(option.option_id) : new Map()
  );
  const handleCheckbox = event => {
    const value = event.target.value;
    if (selectedItems.has(value)) {
      selectedItems.delete(value);
    } else {
      selectedItems.set(value, pickProduct(value));
    }
    if (combine) {
      const name = event.target.name;
      if (selectedItems.has(name)) {
        selectedItems.delete(name);
      } else {
        selectedItems.set(name, pickProduct(name));
      }
      if (option.option_id === 7001028) {
        handleOptionChange(7001109, new Map());
      }
      if (option.option_id === 7001109) {
        handleOptionChange(7001028, new Map());
      }
    }
    handleOptionChange(option.option_id, selectedItems);
  };
  const handleRadio = event => {
    const value = event.target.value;
    if (selectedItems.has(value) && !option.required) {
      setSelectedItems(new Map());
    } else {
      setSelectedItems(new Map([[value, pickProduct(value)]]));
    }
  };
  const change = () => {
    handleOptionChange(option.option_id, selectedItems);
  };
  useEffect(
    () => {
      if (!required) {
        change(option.option_id, selectedItems);
      }
    },
    [selectedItems]
  );
  useEffect(
    () => {
      if (step && !required && currentStep < step) {
        setSelectedItems(new Map());
      }
      if (option.required && option.type === 'select') {
        setSelectedItems(
          new Map([
            [
              option.product_links[0].sku,
              pickProduct(option.product_links[0].sku)
            ]
          ])
        );
      }
    },
    [currentStep]
  );
  const pickProduct = sku => {
    return option.product_links.find(link => link.sku === sku);
  };

  if (combine) {
    const showStrikePriceCombine = has(option, 'originalPrice');

    return (
      <ProductOptionListControl>
        <ProductOptionControl
          isActive={selectedItems.has(option.product_links[0].sku) || required}
          name={option.product_links[1].sku}
          onChange={
            required ? null : type === 'checkbox' ? handleCheckbox : handleRadio
          }
          title={title || option.product_links[0].name}
          type={type}
          value={option.product_links[0].sku}
          controlPosition="center"
          imageElement={
            images && (
              <Grid direction="x">
                <Cell smallSize={12}>
                  <Picture largeImage={images[0]} lazyload={false} />
                </Cell>
                <Cell smallSize={12}>
                  <Picture largeImage={images[1]} lazyload={false} />
                </Cell>
              </Grid>
            )
          }
        >
          {required && option.product_links[0].price && (
            <span styleName="included">
              <strong>{t('Included')}</strong>{' '}
              <Price
                price={option.product_links[0].price}
                showVatMessage={false}
              />
            </span>
          )}
          {!required && option.product_links[0].price && (
            <>
              <Price
                price={
                  (option.product_links[0].price +
                    option.product_links[1].price) *
                  amount
                }
                additionalText={
                  showStrikePriceCombine && (
                    <>
                      {isUKStore && 'RRP: '}
                      <Price
                        price={option.originalPrice * amount}
                        styleName="originalPrice"
                        showVatMessage={false}
                      />
                    </>
                  )
                }
              />
              {amount > 1 && (
                <div styleName="price-per">
                  <p>
                    <Price
                      styleName="inline-price"
                      price={
                        option.product_links[0].price +
                        option.product_links[1].price
                      }
                    />{' '}
                    {` ${t('per set')}`}
                  </p>
                </div>
              )}
              {showStrikePriceCombine && (
                <div style={{ color: '#BD3500' }}>
                  {t('Save')}{' '}
                  <Price
                    price={
                      (option.originalPrice -
                        (option.product_links[0].price +
                          option.product_links[1].price)) *
                      amount
                    }
                    styleName="saveAmount"
                    showVatMessage={false}
                  />
                </div>
              )}
            </>
          )}
          {subtitle && <p styleName="subtitle">{subtitle}</p>}
        </ProductOptionControl>
      </ProductOptionListControl>
    );
  }

  if (!option || !option.product_links) {
    return null;
  }

  return (
    <ProductOptionListControl>
      {option &&
        option.product_links.map((product, i) => {
          // Extract strike prices if opted in via optionData.fetchDetails
          const productDetails =
            linkedProductsWithDetails &&
            linkedProductsWithDetails.find(s => s.sku === product.sku);
          if (productDetails) {
            const specialPrice = productDetails['custom_attributes'].find(
              s => s['attribute_code'] === 'special_price'
            );

            product.strikePrice = specialPrice
              ? productDetails.regular_price
              : null;
          }

          const showStrikePrice =
            product.strikePrice && product.strikePrice !== product.price;

          return (
            <ProductOptionControl
              key={product.name}
              isActive={selectedItems.has(product.sku) || required}
              hatTitle={i === recommended && t('Recommended')}
              name={product.sku}
              onClick={
                required
                  ? null
                  : type === 'checkbox'
                  ? handleCheckbox
                  : handleRadio
              }
              title={title || product.name}
              type={type}
              value={product.sku}
              controlPosition="center"
              imageUrl={images && images[i]}
            >
              {required && product.price && (
                <span styleName="included">
                  <strong>{t('Included')}</strong>{' '}
                  <Price price={product.price * amount} />
                </span>
              )}
              {!required && product.price && (
                <>
                  <Price
                    price={product.price * amount}
                    additionalText={
                      showStrikePrice && (
                        <>
                          {isUKStore && 'RRP: '}
                          <Price
                            price={product.strikePrice * amount}
                            styleName="originalPrice"
                            showVatMessage={false}
                          />
                        </>
                      )
                    }
                  />
                  {amount > 1 && (
                    <div styleName="price-per">
                      <p>
                        <Price
                          styleName="inline-price"
                          price={product.price}
                          showVatMessage={false}
                        />{' '}
                        {` ${forPrinter ? t('per printer') : t('per item')}`}
                      </p>
                    </div>
                  )}
                  {showStrikePrice && (
                    <div style={{ color: '#BD3500' }}>
                      {t('Save')}{' '}
                      <Price
                        price={(product.strikePrice - product.price) * amount}
                        styleName="saveAmount"
                        showVatMessage={false}
                      />
                    </div>
                  )}
                </>
              )}
              {subtitle && <p styleName="subtitle">{subtitle}</p>}
            </ProductOptionControl>
          );
        })}
    </ProductOptionListControl>
  );
};

export default ProductOptionCard;
